// The store logo on a vertical-strip seal. The relay only takes a whole-label
// background PNG, so the tab composes the uploaded logo onto a white canvas the
// size of the label, fitted into the clear box above "ONLY AT THIS LOCATION"
// (DEFAULT_SEAL_TEMPLATE starts that line at y=200), and stores the result as
// the template's background_png_base64.

import { MAX_BACKGROUND_BYTES } from "./boxTopper";

/** Where the logo goes, in printer dots: 616 x 170 at (40, 10). */
export const LOGO_BOX = { x: 40, y: 10, width: 616, height: 170 } as const;
/** Nothing of the logo is ever drawn at or below this line. */
export const LOGO_CLEAR_BOTTOM = 190;
/** Largest logo file accepted before composing. */
export const MAX_LOGO_SOURCE_BYTES = 5_000_000;
export const LOGO_FILE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

export interface LogoPlacement {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The logo scaled down to fit inside LOGO_BOX with its aspect kept (never
 *  cropped, never enlarged past its own pixel size), centered in it both ways. */
export function logoPlacement(sourceWidth: number, sourceHeight: number): LogoPlacement {
  if (!(sourceWidth > 0) || !(sourceHeight > 0)) throw new Error("The logo has no size.");
  const scale = Math.min(1, LOGO_BOX.width / sourceWidth, LOGO_BOX.height / sourceHeight);
  const width = Math.max(1, Math.min(LOGO_BOX.width, Math.round(sourceWidth * scale)));
  const height = Math.max(1, Math.min(LOGO_BOX.height, Math.round(sourceHeight * scale)));
  return {
    x: LOGO_BOX.x + Math.floor((LOGO_BOX.width - width) / 2),
    y: LOGO_BOX.y + Math.floor((LOGO_BOX.height - height) / 2),
    width,
    height,
  };
}

/** Whether the logo is smaller than the box both ways, so it prints at its own
 *  small size rather than filling the box. */
export const isSmallLogo = (sourceWidth: number, sourceHeight: number): boolean =>
  sourceWidth < LOGO_BOX.width && sourceHeight < LOGO_BOX.height;

/** The slice of a 2D canvas the composer uses, so tests can pass a fake. */
export interface LogoCanvas {
  width: number;
  height: number;
  getContext(kind: "2d"): {
    fillStyle: string | CanvasGradient | CanvasPattern;
    imageSmoothingEnabled: boolean;
    imageSmoothingQuality?: ImageSmoothingQuality;
    fillRect(x: number, y: number, w: number, h: number): void;
    drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
  } | null;
  toDataURL(type: "image/png"): string;
}

export type LogoImage = CanvasImageSource & { width: number; height: number };

const PNG_DATA_URL_PREFIX = "data:image/png;base64,";

const browserCanvas = (): LogoCanvas => document.createElement("canvas") as unknown as LogoCanvas;

/** Draws the logo onto a white label-sized canvas (transparent pixels land on
 *  white) and returns the PNG as bare base64, the stored background shape. */
export function composeLogoBackground(
  image: LogoImage,
  label: { width: number; height: number },
  createCanvas: () => LogoCanvas = browserCanvas,
): string {
  const canvas = createCanvas();
  canvas.width = label.width;
  canvas.height = label.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser cannot prepare the logo.");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, label.width, label.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  const at = logoPlacement(image.width, image.height);
  ctx.drawImage(image, at.x, at.y, at.width, at.height);
  const url = canvas.toDataURL("image/png");
  const data = url.startsWith(PNG_DATA_URL_PREFIX) ? url.slice(PNG_DATA_URL_PREFIX.length) : "";
  if (!data) throw new Error("This browser could not prepare the logo. Try a smaller image or another browser.");
  return data;
}

/** Decoded byte size of a base64 string. */
export const base64Bytes = (b64: string): number =>
  Math.floor((b64.length * 3) / 4) - (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);

/** Why a chosen file cannot be used as the logo, or null when it can. */
export function logoFileProblem(file: Pick<File, "type" | "size">): string | null {
  if (!(LOGO_FILE_TYPES as readonly string[]).includes(file.type)) return "Use a PNG, JPEG or WebP image.";
  if (file.size > MAX_LOGO_SOURCE_BYTES) return "That image is over 5 MB. Choose a smaller file.";
  return null;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file could not be read as an image."));
    };
    img.src = url;
  });
}

export interface ComposedLogo {
  png: string;
  small: boolean;
}

/** A chosen logo file as the composed background base64 for a label canvas,
 *  and whether the logo is small enough to print small.
 *  Throws with a plain-language message the tab shows as is. */
export async function logoFileToBackground(file: File, label: { width: number; height: number }): Promise<ComposedLogo> {
  const problem = logoFileProblem(file);
  if (problem) throw new Error(problem);
  const image = await loadImage(file);
  // A detached image's width/height are its natural size.
  const png = composeLogoBackground(image, label);
  if (base64Bytes(png) > MAX_BACKGROUND_BYTES) {
    throw new Error("That logo is too detailed to store. Try a simpler logo or one with fewer colors.");
  }
  return { png, small: isSmallLogo(image.width, image.height) };
}
